import { z } from "zod";
import { uuid } from "./common";
import type { PlatformPermission } from "./billing";

/**
 * Уведомления сотрудникам платформы в Telegram (тот же бот, что у клиентов). Получатель — по праву роли (`permission`);
 * у событий клиента (`perCompany`) — ещё фильтр по менеджеру клиента: менеджер получает события своих клиентов
 * даже без права (`toManager`), отдел продаж — только своих (и, по желанию, клиентов без менеджера).
 */
export const PLATFORM_NOTIFICATION_TYPES = ["lead.created", "company.created", "support.message", "payment.received", "subscription.changed", "subscription.status"] as const;
export type PlatformNotificationType = (typeof PLATFORM_NOTIFICATION_TYPES)[number];

export const PLATFORM_NOTIFICATION_CATALOG: Record<PlatformNotificationType, { permission: PlatformPermission; perCompany: boolean; toManager: boolean; label: string; hint: string }> = {
  "lead.created": { permission: "leads.view", perCompany: false, toManager: false, label: "Новая заявка с сайта", hint: "Кто-то оставил заявку на подключение на сайте" },
  "company.created": { permission: "companies.view", perCompany: true, toManager: true, label: "Новый клиент", hint: "Заведение зарегистрировалось само или его добавили в Platform Admin" },
  "support.message": { permission: "support.view", perCompany: true, toManager: true, label: "Обращение клиента", hint: "Новое обращение в поддержку или новое сообщение в нём" },
  "payment.received": { permission: "billing.view", perCompany: true, toManager: true, label: "Оплата", hint: "Клиент оплатил счёт" },
  "subscription.changed": { permission: "subscriptions.manage", perCompany: true, toManager: true, label: "Клиент сменил тариф или дополнения", hint: "Клиент сам подключил или отключил что-то в разделе «Подписка»" },
  "subscription.status": { permission: "billing.view", perCompany: true, toManager: true, label: "Проблемы с оплатой", hint: "Закончился пробный период, подписка не оплачена, доступ ограничен или приостановлен" },
};

/** Чьи клиенты: все; только мои; выбранных менеджеров. `unassigned` — ещё и клиенты без менеджера. */
export const PLATFORM_NOTIFY_CLIENTS = ["all", "mine", "selected"] as const;
export type PlatformNotifyClients = (typeof PLATFORM_NOTIFY_CLIENTS)[number];

export const platformNotifyPrefsSchema = z.strictObject({
  /** Отключённые типы */
  muted: z.array(z.enum(PLATFORM_NOTIFICATION_TYPES)).max(PLATFORM_NOTIFICATION_TYPES.length),
  clients: z.enum(PLATFORM_NOTIFY_CLIENTS),
  managerIds: z.array(uuid).max(100),
  unassigned: z.boolean(),
}).refine((v) => v.clients !== "selected" || v.managerIds.length > 0 || v.unassigned, { path: ["managerIds"], error: "Выберите хотя бы одного менеджера" });
export type PlatformNotifyPrefs = z.output<typeof platformNotifyPrefsSchema>;

/** По умолчанию: отдел продаж — свои клиенты и клиенты без менеджера, остальные роли — все клиенты. */
export const defaultPlatformNotifyPrefs = (role: string): PlatformNotifyPrefs =>
  role === "sales" ? { muted: [], clients: "mine", managerIds: [], unassigned: true } : { muted: [], clients: "all", managerIds: [], unassigned: true };

/** Отдел продаж видит в уведомлениях только своих клиентов (и клиентов без менеджера): чужие оплаты и обращения ему не нужны. */
export const platformNotifyPrefsFor = (role: string, saved: Partial<PlatformNotifyPrefs> | null | undefined): PlatformNotifyPrefs => {
  const p = { ...defaultPlatformNotifyPrefs(role), ...saved };
  return role === "sales" ? { ...p, clients: "mine", managerIds: [] } : p;
};

/** Типы, доступные роли: по праву или как менеджеру клиента (менеджером бывают owner, admin, sales). */
export const platformNotifyTypesFor = (role: string, can: (p: PlatformPermission) => boolean, canBeManager: boolean) =>
  PLATFORM_NOTIFICATION_TYPES.filter((t) => can(PLATFORM_NOTIFICATION_CATALOG[t].permission) || (canBeManager && PLATFORM_NOTIFICATION_CATALOG[t].toManager));

/**
 * Получит ли сотрудник событие клиента с менеджером `managerId` (null — без менеджера). Право — у роли (`hasPermission`),
 * свой клиент — всегда (если тип `toManager`), отдел продаж — только свои и, если включено, без менеджера.
 */
export function platformNotifyMatches(
  type: PlatformNotificationType, user: { id: string; role: string; hasPermission: boolean; prefs: PlatformNotifyPrefs }, managerId: string | null,
): boolean {
  const c = PLATFORM_NOTIFICATION_CATALOG[type];
  if (user.prefs.muted.includes(type)) return false;
  if (!c.perCompany) return user.hasPermission;
  const mine = managerId !== null && managerId === user.id;
  if (mine) return user.hasPermission || c.toManager;
  if (!user.hasPermission) return false;
  if (managerId === null) return user.prefs.clients === "all" || user.prefs.unassigned;
  if (user.prefs.clients === "all") return true;
  if (user.prefs.clients === "selected") return user.prefs.managerIds.includes(managerId);
  return false;
}
